import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  InputAdornment,
  Collapse,
  Divider,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api';

const THEMES = [
  'leadership', 'ownership', 'collaboration', 'communication',
  'problem-solving', 'conflict-resolution', 'data-driven',
  'adaptability', 'customer-focus', 'innovation',
];

const THEME_COLORS = {
  leadership: '#6366f1',
  ownership: '#f97316',
  collaboration: '#10b981',
  communication: '#06b6d4',
  'problem-solving': '#8b5cf6',
  'conflict-resolution': '#ef4444',
  'data-driven': '#3b82f6',
  adaptability: '#f59e0b',
  'customer-focus': '#ec4899',
  innovation: '#14b8a6',
};

function StoryCard({ story, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(null); // null | 'full' | 'interview'

  const toggle = (mode) => setExpanded(prev => prev === mode ? null : mode);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(99,102,241,0.15)',
        bgcolor: '#131929',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'rgba(99,102,241,0.3)' },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'var(--tx-hi)', lineHeight: 1.4, mb: 0.75 }}
            >
              {story.title}
            </Typography>

            {/* Alternative titles */}
            {story.alternative_titles?.length > 0 && (
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--tx-dim)', mb: 0.5 }}>
                Also known as: {story.alternative_titles.join(' · ')}
              </Typography>
            )}

            {/* Tags */}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {story.themes?.map(t => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    bgcolor: `${THEME_COLORS[t] || 'var(--tx-ft)'}22`,
                    color: THEME_COLORS[t] || 'var(--tx-lo)',
                    border: `1px solid ${THEME_COLORS[t] || 'var(--tx-ft)'}44`,
                  }}
                />
              ))}
              {story.tags?.map(t => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    color: 'var(--tx-dim)',
                    borderColor: 'rgba(255,255,255,0.08)',
                  }}
                />
              ))}
            </Stack>

            {/* Meta */}
            <Typography variant="caption" sx={{ color: 'var(--tx-ft)' }}>
              {story.source_filename ? `From: ${story.source_filename} · ` : ''}
              {new Date(story.created_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            <Tooltip title="Edit story">
              <IconButton size="small" onClick={() => onEdit(story)} sx={{ color: 'var(--tx-dim)', '&:hover': { color: '#6366f1' } }}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete story">
              <IconButton size="small" onClick={() => onDelete(story.id)} sx={{ color: 'var(--tx-dim)', '&:hover': { color: '#ef4444' } }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Toggle buttons */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            size="small"
            startIcon={<ArticleOutlinedIcon />}
            onClick={() => toggle('full')}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded === 'full' ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            sx={{
              fontSize: '0.75rem',
              color: expanded === 'full' ? '#6366f1' : 'var(--tx-dim)',
              borderColor: expanded === 'full' ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.08)',
              border: '1px solid',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.4,
              textTransform: 'none',
              '&:hover': { borderColor: 'rgba(99,102,241,0.4)', color: '#6366f1' },
            }}
          >
            Full Story
          </Button>
          <Button
            size="small"
            startIcon={<RecordVoiceOverOutlinedIcon />}
            onClick={() => toggle('interview')}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded === 'interview' ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            sx={{
              fontSize: '0.75rem',
              color: expanded === 'interview' ? '#f97316' : 'var(--tx-dim)',
              borderColor: expanded === 'interview' ? 'rgba(249,115,22,0.4)' : 'rgba(255,255,255,0.08)',
              border: '1px solid',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.4,
              textTransform: 'none',
              '&:hover': { borderColor: 'rgba(249,115,22,0.4)', color: '#f97316' },
            }}
          >
            Interview Form
          </Button>
        </Stack>

        {/* Full story */}
        <Collapse in={expanded === 'full'}>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
          <Typography
            variant="body2"
            sx={{
              color: '#cbd5e1',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
              fontSize: '0.875rem',
            }}
          >
            {story.full_story}
          </Typography>
        </Collapse>

        {/* Interview form */}
        <Collapse in={expanded === 'interview'}>
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.06)' }} />
          <Box
            sx={{
              bgcolor: 'rgba(249,115,22,0.05)',
              border: '1px solid rgba(249,115,22,0.15)',
              borderRadius: 1.5,
              p: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#f97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}
            >
              Read aloud during interview
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'var(--tx-md)',
                lineHeight: 2,
                whiteSpace: 'pre-wrap',
                fontSize: '0.875rem',
              }}
            >
              {story.interview_form}
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

const BLANK_STORY = {
  title: '',
  alternative_titles: [],
  tags: '',
  themes: [],
  full_story: '',
  interview_form: '',
  situation: '',
  task: '',
  action: '',
  result: '',
};

export default function InterviewPrepPage() {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterTheme, setFilterTheme] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editStory, setEditStory] = useState(null); // null = create mode
  const [editForm, setEditForm] = useState(BLANK_STORY);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchStories(); }, []);

  async function fetchStories() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/interview/stories');
      setStories(res.data.stories || []);
    } catch (err) {
      setError('Failed to load stories');
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return stories.filter(s => {
      const matchesSearch = !q ||
        s.title?.toLowerCase().includes(q) ||
        s.tags?.some(t => t.toLowerCase().includes(q)) ||
        s.full_story?.toLowerCase().includes(q);
      const matchesTheme = !filterTheme || s.themes?.includes(filterTheme);
      return matchesSearch && matchesTheme;
    });
  }, [stories, search, filterTheme]);

  function openCreate() {
    setEditStory(null);
    setEditForm(BLANK_STORY);
    setEditOpen(true);
  }

  function openEdit(story) {
    setEditStory(story);
    setEditForm({
      title: story.title || '',
      alternative_titles: story.alternative_titles || [],
      tags: (story.tags || []).join(', '),
      themes: story.themes || [],
      full_story: story.full_story || '',
      interview_form: story.interview_form || '',
      situation: story.situation || '',
      task: story.task || '',
      action: story.action || '',
      result: story.result || '',
    });
    setEditOpen(true);
  }

  async function handleSave() {
    if (!editForm.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (editStory) {
        await api.put(`/api/interview/stories/${editStory.id}`, payload);
      } else {
        await api.post('/api/interview/stories', payload);
      }
      setEditOpen(false);
      await fetchStories();
    } catch {
      // keep dialog open on error
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/api/interview/stories/${id}`);
      setDeleteConfirm(null);
      await fetchStories();
    } catch {
      setError('Failed to delete story');
    }
  }

  return (
    <Box sx={{ bgcolor: '#0d1117', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--tx-hi)' }}>
              Stories Bank
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--tx-dim)', mt: 0.25 }}>
              {stories.length} {stories.length === 1 ? 'story' : 'stories'} saved
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => navigate('/interview/upload')}
            sx={{
              borderColor: 'rgba(99,102,241,0.4)',
              color: '#818cf8',
              '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.08)' },
            }}
          >
            Upload / Extract
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            Add Story
          </Button>
        </Box>

        {/* Search + filter */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search stories…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'var(--tx-ft)', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Theme filter chips */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setFilterTheme('')}
            sx={{
              bgcolor: !filterTheme ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
              color: !filterTheme ? '#818cf8' : 'var(--tx-dim)',
              border: '1px solid',
              borderColor: !filterTheme ? 'rgba(99,102,241,0.4)' : 'transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.7rem',
            }}
          />
          {THEMES.map(t => (
            <Chip
              key={t}
              label={t}
              size="small"
              onClick={() => setFilterTheme(prev => prev === t ? '' : t)}
              sx={{
                bgcolor: filterTheme === t ? `${THEME_COLORS[t]}22` : 'rgba(255,255,255,0.04)',
                color: filterTheme === t ? THEME_COLORS[t] : 'var(--tx-dim)',
                border: '1px solid',
                borderColor: filterTheme === t ? `${THEME_COLORS[t]}44` : 'transparent',
                cursor: 'pointer',
                fontSize: '0.7rem',
              }}
            />
          ))}
        </Stack>

        {/* Content */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress size={32} sx={{ color: '#6366f1' }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" sx={{ color: 'var(--tx-gh)', mb: 1 }}>
              {stories.length === 0 ? 'No stories yet' : 'No stories match your search'}
            </Typography>
            {stories.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => navigate('/interview/upload')}
                sx={{ mt: 1, borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8' }}
              >
                Upload a document to extract stories
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filtered.map(story => (
              <Grid key={story.id} size={{ xs: 12, md: 6 }}>
                <StoryCard
                  story={story}
                  onEdit={openEdit}
                  onDelete={id => setDeleteConfirm(id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Edit / Create dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#131929', border: '1px solid rgba(99,102,241,0.2)' } }}
      >
        <DialogTitle sx={{ color: 'var(--tx-hi)', fontWeight: 700 }}>
          {editStory ? 'Edit Story' : 'Add Story'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Title"
              value={editForm.title}
              onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
              fullWidth
              size="small"
              required
            />

            {/* Alternative titles */}
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--tx-dim)', display: 'block', mb: 0.75 }}>
                Alternative Titles
              </Typography>
              <Stack spacing={0.75}>
                {(editForm.alternative_titles || []).map((alt, ai) => (
                  <Stack key={ai} direction="row" alignItems="center" gap={0.75}>
                    <TextField
                      value={alt}
                      onChange={e => {
                        const next = [...editForm.alternative_titles];
                        next[ai] = e.target.value;
                        setEditForm(f => ({ ...f, alternative_titles: next }));
                      }}
                      fullWidth
                      size="small"
                      placeholder="Alternative title…"
                    />
                    <IconButton
                      size="small"
                      onClick={() => setEditForm(f => ({ ...f, alternative_titles: f.alternative_titles.filter((_, i) => i !== ai) }))}
                      sx={{ color: 'var(--tx-ft)', '&:hover': { color: '#ef4444' }, flexShrink: 0 }}
                    >
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => setEditForm(f => ({ ...f, alternative_titles: [...(f.alternative_titles || []), ''] }))}
                sx={{ mt: 0.75, color: '#818cf8', textTransform: 'none', fontSize: '0.75rem', px: 0.5, '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' } }}
              >
                Add alternative title
              </Button>
            </Box>

            <TextField
              label="Tags (comma-separated)"
              value={editForm.tags}
              onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))}
              fullWidth
              size="small"
              placeholder="leadership, product, fintech"
            />
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--tx-dim)', mb: 0.75, display: 'block' }}>
                Themes
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {THEMES.map(t => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    onClick={() => setEditForm(f => ({
                      ...f,
                      themes: f.themes.includes(t) ? f.themes.filter(x => x !== t) : [...f.themes, t],
                    }))}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: editForm.themes.includes(t) ? `${THEME_COLORS[t]}22` : 'rgba(255,255,255,0.04)',
                      color: editForm.themes.includes(t) ? THEME_COLORS[t] : 'var(--tx-dim)',
                      border: '1px solid',
                      borderColor: editForm.themes.includes(t) ? `${THEME_COLORS[t]}44` : 'transparent',
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Stack>
            </Box>
            <TextField
              label="Full Story"
              value={editForm.full_story}
              onChange={e => setEditForm(f => ({ ...f, full_story: e.target.value }))}
              fullWidth
              multiline
              minRows={5}
              placeholder="The complete narrative with all details, data points, and context…"
            />
            <TextField
              label="Interview Form"
              value={editForm.interview_form}
              onChange={e => setEditForm(f => ({ ...f, interview_form: e.target.value }))}
              fullWidth
              multiline
              minRows={4}
              placeholder="• Bullet points you can read aloud naturally during an interview…"
              helperText="Formatted as bullet points for verbal delivery"
            />
            <Grid container spacing={1.5}>
              {['situation', 'task', 'action', 'result'].map(field => (
                <Grid key={field} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                    value={editForm[field]}
                    onChange={e => setEditForm(f => ({ ...f, [field]: e.target.value }))}
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: 'var(--tx-dim)' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editForm.title.trim()}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {saving ? <CircularProgress size={18} /> : editStory ? 'Save Changes' : 'Add Story'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        PaperProps={{ sx: { bgcolor: '#131929', border: '1px solid rgba(255,255,255,0.08)' } }}
      >
        <DialogTitle sx={{ color: 'var(--tx-hi)' }}>Delete story?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--tx-lo)' }}>This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: 'var(--tx-dim)' }}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirm)} sx={{ color: '#ef4444' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
